import { Component, OnInit, ViewChild } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { CoreService, DialogResult, DialogService, DspxCardSetBaseFormComponent, SpinnerService } from 'dspx-core';
import { finalize, take } from 'rxjs';
import { API_ENDPOINTS } from 'src/app/constants/api-endpoints.constants';
import { FILTER_QUESTION_CODES, NAV_TAB_NUMBERS, PAGE_NUMBERS, POP_UP_CODES, QUESTION_CODES, REFERENCE_DATA_IDS, SAVE_RESULT_STATUS, SECTION_CODES } from 'src/app/constants/app-data.constants';
import { ROUTE_CONSTANTS } from 'src/app/constants/route.constants';
import { TEMPLATES } from 'src/app/constants/templates.constants';
import { Mapper, MapperBuilder, TRANSFORM } from 'src/app/helpers/mapper';
import { ConcessionsApplicationGridFilterRequest } from 'src/app/interfaces/concessions-application-grid-filter-request.interface';
import { AppDataService } from 'src/app/services/app-data.service';
import { ErrorService } from 'src/app/services/error.service';
import { ResponseParserService } from 'src/app/services/response-parser.service';
import { BaseCardSetListComponent } from '../base-card-set-list-component/base-card-set-list.component';

@Component({
  selector: 'app-concessions-application-list',
  templateUrl: './concessions-application-list.component.html',
  styleUrl: './concessions-application-list.component.scss'
})
export class ConcessionsApplicationListComponent extends BaseCardSetListComponent implements OnInit {
  getConcessionsApplications: boolean = false;
  @ViewChild(DspxCardSetBaseFormComponent)
  private set _baseForm(value: DspxCardSetBaseFormComponent) {
    this.baseForm = value
  }

  filterQuestionToFilterModelMapper: Mapper;
  activeDialog: string = '';
  selectedApplicationItems: any[] = [];
  constructor(
    protected router: Router,
    protected spinnerService: SpinnerService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected appDataService: AppDataService,
    private coreService: CoreService,
    private errorService: ErrorService
  ) {
    super(
      router,
      spinnerService,
      dialogService,
      responseParserService,
      appDataService,
      TEMPLATES.concessionsApplicationList,
      () => this.getListApiEndPoint(router),
      false);

    this.filterQuestionToFilterModelMapper =
      new MapperBuilder()
        .WithDefaultTargetFieldMapping(/Filter$/i, "") // When target field name is not specified, just use the source field's name, but drop "Filter" from the end
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.applicationQuickSearchTerm)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.applicationReferenceNumberFilter)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.applicationTypeFilter)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.applicationStatusFilter)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.applicationAssignedToFilter)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.applicationSubmittedOnFromFilter, TRANSFORM.STRING_TO_DATE)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.applicationSubmittedOnToFilter, TRANSFORM.STRING_TO_DATE)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.applicationYearFilter)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.residentialAddressFilter)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.paidAmountFilter)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.datePaidFilter, TRANSFORM.STRING_TO_DATE)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.applicationSourceFilter)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.applicationFlaggedForReviewFilter)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.applicationBulkUploadIdFilter)
        .MapFrom(FILTER_QUESTION_CODES.concessionsApplicationListForm.applicationPendingReasonFilter, TRANSFORM.IDENTITY)
        .BuildMapper();
  }

  getListApiEndPoint(router: Router): string {
    if (this.getConcessionsApplications) {
      return API_ENDPOINTS.dashboard.getConcessionsApplications;
    }

    if (router.url.indexOf(ROUTE_CONSTANTS.concessionsApplication.list.myApplications) >= 0) {
      return API_ENDPOINTS.dashboard.getMyConcessionsApplications;
    }
    else if (router.url.indexOf(ROUTE_CONSTANTS.concessionsApplication.list.pendingReview) >= 0) {
      return API_ENDPOINTS.dashboard.getConcessionsApplicationsPendingReview;
    }
    throw new Error(`Invalid router url ${router.url}`);
  }

  ngOnInit(): void {
  }

  formLoad(event: any): void {
    if (history.state.applicationFilterRequest == null) {
      this.loadGridDataOnFormLoad();
    }
    else {
      this.loadGridDataOnFormLoad(history.state.applicationFilterRequest);
    }
  }

  filterData(event: any): void {
    switch (event.field.key) {
      case QUESTION_CODES.concessionsApplicationListForm.applicationListFilter:
      case QUESTION_CODES.concessionsApplicationListForm.applicationQuickSearchTerm:
        {
          this.getConcessionsApplications = true;
          const applicationFilterRequest = this.getApplicationFilterRequest();
          this.loadGridData(applicationFilterRequest);
          break;
        }
    }
  }

  actionButton_applicationPlaceholder_datagrid_colLink_referenceNumber_Clicked($event: any): void {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/', ROUTE_CONSTANTS.concessionsApplication.prefix, ROUTE_CONSTANTS.concessionsApplicationDetail.prefix, $event.event.data.id]));
    window.open(url, '_blank');
  }

  actionButton_applicationPlaceholder_datagrid_view_Clicked(event: any): void {
    const navExtras = this.getNavigationExtras();
    this.router.navigate(['/', ROUTE_CONSTANTS.concessionsApplication.prefix, ROUTE_CONSTANTS.concessionsApplicationDetail.prefix, event.event.data.id], navExtras);
  }

  actionButton_applicationPlaceholder_datagrid_edit_Clicked(event: any): void {
    const applicationId = event.event.data.id;
    const navExtras = this.getNavigationExtras();

    switch (event.event.data.applicationTypeId) {
      case REFERENCE_DATA_IDS.applicationType.nonMainsWater:
        this.router.navigate(['/', ROUTE_CONSTANTS.concessionsApplication.prefix, ROUTE_CONSTANTS.concessionsApplicationNonMainsWater.prefix, applicationId], navExtras);
        break;
      case REFERENCE_DATA_IDS.applicationType.nonMainsEnergy:
        this.router.navigate(['/', ROUTE_CONSTANTS.concessionsApplication.prefix, ROUTE_CONSTANTS.concessionsApplicationNonMainsEnergy.prefix, applicationId], navExtras);
        break;
      case REFERENCE_DATA_IDS.applicationType.nonMainsMedicalCooling:
        this.router.navigate(['/', ROUTE_CONSTANTS.concessionsApplication.prefix, ROUTE_CONSTANTS.concessionsApplicationNonMainsMedicalCooling.prefix, applicationId], navExtras);
        break;
      case REFERENCE_DATA_IDS.applicationType.nonMainsLifeSupport:
        this.router.navigate(['/', ROUTE_CONSTANTS.concessionsApplication.prefix, ROUTE_CONSTANTS.concessionsApplicationNonMainsLifeSupport.prefix, applicationId], navExtras);
        break;
      case REFERENCE_DATA_IDS.applicationType.edapowGoldCardMunicipalRates:
        this.router.navigate(['/', ROUTE_CONSTANTS.concessionsApplication.prefix, ROUTE_CONSTANTS.concessionsApplicationEdaAndPowGoldCardMunicipalRates.prefix, applicationId], navExtras);
        break;
    }
  }

  actionButton_applicationPlaceholder_datagrid_viewdetails_Clicked(event: any): void {
    this.actionButton_applicationPlaceholder_datagrid_edit_Clicked(event);
  }

  actionButton_applicationPlaceholder_datagrid_delete_Clicked(event: any): void {
    const application = event.event.data;
    this.deleteApplication(application.id);
  }

  actionButton_applicationPlaceholder_datagrid_custom_bulkAssign_Clicked(customEvent: any): void {
    this.selectedApplicationItems = customEvent.event.data.selectedItems || [];
    
    if (!this.selectedApplicationItems || this.selectedApplicationItems.length === 0) {
      this.showApplicationSelectionAlert();
      return;
    }

    this.activeDialog = POP_UP_CODES.vptasApplicationDetails.bulkAssignApplicationUserPopup;
    this.dialogService.showDialog(this.activeDialog);
  }
      
  dialogButton_bulkAssignApplicationUserPopup_Clicked($event: any): void {
    
    if (!$event || !$event.assignedToUserIdPopup) {
      return;
    }
    
    const ids = this.selectedApplicationItems.map(r => r.id);
    const request = {
      ids: ids,
      assignedToUserId: $event.assignedToUserIdPopup
    };
    
    this.spinnerService.showSpinner();
    this.appDataService.post(API_ENDPOINTS.concessionsApplication.bulkAssign, request)
      .pipe(
        take(1), 
        this.errorService.errorHandler(),
        finalize(() => this.spinnerService.hideSpinner())
      )
      .subscribe((response) => {
        this.onBulkAssignSuccess(response);
      });
  }
  
  onBulkAssignSuccess(response: any) {
    this.spinnerService.hideSpinner();
    if (response?.status === SAVE_RESULT_STATUS.success) {
      this.dialogService
        .showSimplePopup("Application(s) assigned successfully.", "Success!", "Ok")
        .subscribe(() => {
          const filterRequest = this.getApplicationFilterRequest();
          this.loadGridDataOnFormLoad(filterRequest);
        });
    }
  }

    showApplicationSelectionAlert() {
    this.dialogService
      .showSimplePopup("Please select at least one application.", "Application Alert!", "Ok")
      .subscribe(
        result => {
        }
      );
  }

  private deleteApplication(id) {
    this.dialogService
      .showConfirm(POP_UP_CODES.concessionsApplicationList.deleteApplicationConfirmation)
      .pipe(take(1))
      .subscribe(data => {
        switch (data as DialogResult) {
          case DialogResult.ACCEPTED: {
            this.spinnerService.showSpinner();
            this.appDataService
            .postAndFetchData(`${API_ENDPOINTS.concessionsApplication.delete}/${id}`)
              .pipe(take(1), this.errorService.errorHandler(),
                finalize(() => this.spinnerService.hideSpinner))
              .subscribe(response => {
                this.onDeleteApplicationSuccess(response);
              });
            break;
          }
          case DialogResult.REJECTED: {
            break;
          }
        }
      });
  }

  onDeleteApplicationSuccess(response: any) {
    this.spinnerService.hideSpinner();
    if (response?.status === SAVE_RESULT_STATUS.success) {
      this.loadGridData(this.loadGridDataOnFormLoad())
    }
  }

  private getApplicationFilterRequest(): ConcessionsApplicationGridFilterRequest {
    let filterModel = this.getQuestionValue(
      this.model,
      NAV_TAB_NUMBERS.concessionsApplicationListForm.applications,
      PAGE_NUMBERS.concessionsApplicationListForm.applications,
      SECTION_CODES.concessionsApplicationListForm.application,
      QUESTION_CODES.concessionsApplicationListForm.applicationListFilter
    );
    let mappedFilters = this.filterQuestionToFilterModelMapper.Map(filterModel);

    const applicationQuickSearchTermValue = this.getQuestionValue(
      this.model,
      NAV_TAB_NUMBERS.concessionsApplicationListForm.applications,
      PAGE_NUMBERS.concessionsApplicationListForm.applications,
      SECTION_CODES.concessionsApplicationListForm.application,
      QUESTION_CODES.concessionsApplicationListForm.applicationQuickSearchTerm
    );

    mappedFilters.applicationQuickSearchTerm = applicationQuickSearchTermValue;
    return mappedFilters;
  }

  private getNavigationExtras(): NavigationExtras {
    const filterRequest = this.getApplicationFilterRequest();

    return {
      state: {
        applicationFilterRequest: filterRequest
      }
    };
  }
}
